import React, { useEffect, useMemo, useState } from 'react'
import { lotteryService, type TenantRuleContentItem, type TenantRuleScope } from '../services/lotteryService'

interface RulesGuideModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  scope?: TenantRuleScope
  fallbackRules?: React.ReactNode
  fallbackGuides?: React.ReactNode
  dialogClassName?: string
  closeButtonLabel?: string
}

function replaceScopeVariables(value: string, scope?: TenantRuleScope): string {
  return String(value || '')
    .replace(/\{\{HUAY_NAME\}\}/g, String(scope?.huayName || '').trim())
    .replace(/\{\{HUAY_TYPE_NAME\}\}/g, String(scope?.huayTypeName || '').trim())
}

function getScopeLabel(scope?: TenantRuleScope): string {
  const parts = [scope?.huayName, scope?.huayTypeName].map((value) => String(value || '').trim()).filter(Boolean)
  return parts.join(' • ')
}

function renderRuleContent(item: TenantRuleContentItem, scope?: TenantRuleScope) {
  const content = replaceScopeVariables(item.content, scope)
  if (item.contentType === 'text' || item.contentType === 'markdown') {
    return <div className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{content}</div>
  }

  return <div className="text-sm" dangerouslySetInnerHTML={{ __html: content }} />
}

export const RulesGuideModal: React.FC<RulesGuideModalProps> = ({
  isOpen,
  onClose,
  title = 'กฏและกติกาการเล่น',
  scope,
  fallbackRules,
  fallbackGuides,
  dialogClassName = 'modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable',
  closeButtonLabel = 'ปิด',
}) => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<{ rules: TenantRuleContentItem[]; guides: TenantRuleContentItem[] }>({ rules: [], guides: [] })

  useEffect(() => {
    if (!isOpen) return

    let isMounted = true
    setLoading(true)
    void lotteryService.getTenantRulesAndGuides(scope).then((nextItems) => {
      if (isMounted) setItems(nextItems)
    }).finally(() => {
      if (isMounted) setLoading(false)
    })

    return () => {
      isMounted = false
    }
  }, [isOpen, scope?.huayId, scope?.huayName, scope?.huayTypeId, scope?.huayTypeName, scope?.tenantId])

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  const hasFetchedRules = items.rules.length > 0
  const hasFetchedGuides = items.guides.length > 0
  const showRulesSection = hasFetchedRules || Boolean(fallbackRules)
  const showGuidesSection = hasFetchedGuides || Boolean(fallbackGuides)
  const hasAnyContent = showRulesSection || showGuidesSection
  const scopeLabel = useMemo(() => getScopeLabel(scope), [scope])

  const renderedRules = useMemo(() => items.rules.map((item, index) => (
    <div key={item._id || item.code || index} className={index > 0 ? 'mt-4 pt-3 border-top' : ''}>
      {item.title ? (
        <p className="mb-2">
          <strong>{replaceScopeVariables(item.title, scope)}</strong>
        </p>
      ) : null}
      {renderRuleContent(item, scope)}
    </div>
  )), [items.rules, scope])

  const renderedGuides = useMemo(() => items.guides.map((item, index) => (
    <div key={item._id || item.code || index} className={index > 0 ? 'mt-4 pt-4 border-top' : ''}>
      {item.title ? (
        <p className="text-center mb-3">
          <strong><u>{replaceScopeVariables(item.title, scope)}</u></strong>
        </p>
      ) : null}
      {renderRuleContent(item, scope)}
    </div>
  )), [items.guides, scope])

  if (!isOpen) return null

  return (
    <div
      className="modal d-block rules-guide-modal"
      tabIndex={-1}
      style={{ background: 'rgba(2, 6, 23, 0.8)', backdropFilter: 'blur(6px)', overflowY: 'auto', padding: '16px 12px' }}
      onClick={onClose}
    >
      <style>{`
        .rules-guide-modal {
          padding-top: 84px !important;
          padding-bottom: 16px !important;
        }
        .rules-guide-modal .rules-guide-scroll {
          scrollbar-width: thin;
          scrollbar-color: color-mix(in srgb, var(--htd-primary) 55%, transparent) transparent;
          color: #0f274a !important;
        }
        .rules-guide-modal .rules-guide-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .rules-guide-modal .rules-guide-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, var(--htd-primary), var(--htd-secondary));
          border-radius: 999px;
        }
        /* หน้า lobby/htd-lh บังคับ color ขาว — override เฉพาะที่ "ไม่ได้" กำหนดสี inline เอง
           (เก็บสีแดง/สีที่ผู้ดูแลตั้งไว้ใน content ให้เหมือนเดิม) */
        .rules-guide-modal .rules-guide-body,
        .rules-guide-modal .rules-guide-body *:not([style*="color"]):not([color]) {
          color: #0f274a !important;
        }
        .rules-guide-modal .rules-guide-body a:not([style*="color"]) {
          color: #0369a1 !important;
          text-decoration: underline;
        }
        .rules-guide-modal .rules-guide-body strong:not([style*="color"]),
        .rules-guide-modal .rules-guide-body b:not([style*="color"]) {
          color: #0b1f3a !important;
          font-weight: 800;
        }
        .rules-guide-modal .rules-guide-body .text-muted:not([style*="color"]),
        .rules-guide-modal .rules-guide-body small:not([style*="color"]),
        .rules-guide-modal .rules-guide-body .small:not([style*="color"]) {
          color: #475569 !important;
        }
        .rules-guide-modal .rules-guide-body table:not([style*="color"]),
        .rules-guide-modal .rules-guide-body th:not([style*="color"]),
        .rules-guide-modal .rules-guide-body td:not([style*="color"]) {
          color: #0f274a !important;
        }
        .rules-guide-modal .rules-guide-body table,
        .rules-guide-modal .rules-guide-body th,
        .rules-guide-modal .rules-guide-body td {
          border-color: #cbd5e1 !important;
        }
        /* เผื่อ content ใช้ class สีแดงแทน inline style */
        .rules-guide-modal .rules-guide-body .text-danger,
        .rules-guide-modal .rules-guide-body .text-red,
        .rules-guide-modal .rules-guide-body font[color] {
          color: #dc2626 !important;
        }
        .rules-guide-modal .rules-guide-body table {
          width: 100%;
          min-width: 520px;
        }
        .rules-guide-modal .rules-guide-body img {
          max-width: 100%;
          height: auto;
          border-radius: 12px;
        }
        .rules-guide-modal .rules-guide-body p:last-child,
        .rules-guide-modal .rules-guide-body ul:last-child,
        .rules-guide-modal .rules-guide-body ol:last-child,
        .rules-guide-modal .rules-guide-body table:last-child {
          margin-bottom: 0;
        }
        .rules-guide-modal .rules-guide-kicker {
          color: var(--htd-primary) !important;
        }
        .rules-guide-modal .rules-guide-kicker.is-guide {
          color: var(--htd-secondary) !important;
        }
        .rules-guide-modal .rules-guide-chip {
          color: #0b5f9a !important;
        }
        .rules-guide-modal .rules-guide-chip.is-guide {
          color: var(--htd-secondary) !important;
        }
        .rules-guide-modal .modal-title {
          color: #fff !important;
          font-weight: 800;
        }
        .rules-guide-modal .rules-guide-header .small {
          color: rgba(226, 232, 240, 0.78) !important;
        }
        .rules-guide-modal .rules-guide-loading,
        .rules-guide-modal .rules-guide-empty {
          color: #334155 !important;
        }
        @media (max-width: 767.98px) {
          .rules-guide-modal {
            padding-top: 58px !important;
          }
          .rules-guide-modal .rules-guide-dialog {
            min-height: auto !important;
          }
          .rules-guide-modal .rules-guide-header,
          .rules-guide-modal .rules-guide-footer {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
        }
      `}</style>
      <div
        className={`${dialogClassName} rules-guide-dialog`}
        style={{ maxWidth: 920, margin: '0 auto', minHeight: 'auto', display: 'flex', alignItems: 'flex-start' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content border-0"
          style={{
            width: '100%',
            borderRadius: 22,
            overflow: 'hidden',
            border: '1px solid rgba(125, 211, 252, 0.3)',
            boxShadow: '0 28px 70px rgba(0, 0, 0, 0.45)',
            background: 'linear-gradient(180deg, rgba(12, 24, 52, 0.98) 0%, rgba(9, 16, 36, 0.98) 100%)',
          }}
        >
          <div
            className="modal-header rules-guide-header"
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 3,
              background: 'linear-gradient(135deg, var(--htd-primary) 0%, var(--htd-secondary) 100%)',
              borderBottom: '1px solid rgba(125, 211, 252, 0.2)',
              padding: '14px 20px',
            }}
          >
            <div>
              <h5 className="modal-title mb-1">{title}</h5>
              <div className="small">
                {scopeLabel || 'รวมกฎและวิธีดูผลไว้ในหน้าต่างเดียว'}
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              aria-label="Close"
              onClick={onClose}
              style={{ borderRadius: '999px', opacity: 0.9 }}
            />
          </div>
          <div
            className="modal-body rules-guide-scroll"
            style={{
              padding: 0,
              maxHeight: 'calc(100vh - 240px)',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              backgroundColor: 'rgba(248, 250, 252, 0.98)',
            }}
          >
            {loading ? (
              <div className="text-center py-5 rules-guide-loading">กำลังโหลดข้อมูล...</div>
            ) : hasAnyContent ? (
              <div className="p-3 p-md-4 rules-guide-body">
                {showRulesSection && (
                  <section
                    className="mb-4"
                    style={{
                      backgroundColor: '#fff',
                      border: '1px solid color-mix(in srgb, var(--htd-primary) 18%, transparent)',
                      borderRadius: 20,
                      padding: '18px 18px 14px',
                      boxShadow: '0 8px 24px color-mix(in srgb, var(--htd-primary) 6%, transparent)',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <div>
                        <div className="small fw-bold text-uppercase rules-guide-kicker" style={{ letterSpacing: '0.08em' }}>
                          Rules
                        </div>
                        <div className="fw-semibold">กฎและข้อกำหนด</div>
                      </div>
                      <div
                        className="small rules-guide-chip"
                        style={{ backgroundColor: 'color-mix(in srgb, var(--htd-primary) 12%, transparent)', padding: '6px 10px', borderRadius: 999 }}
                      >
                        อ่านก่อนส่งโพย
                      </div>
                    </div>
                    <div style={{ lineHeight: 1.7 }}>
                      {hasFetchedRules ? renderedRules : fallbackRules}
                    </div>
                  </section>
                )}
                {showGuidesSection && (
                  <section
                    style={{
                      backgroundColor: '#fff',
                      border: '1px solid color-mix(in srgb, var(--htd-secondary) 16%, transparent)',
                      borderRadius: 20,
                      padding: '18px 18px 14px',
                      boxShadow: '0 8px 24px color-mix(in srgb, var(--htd-secondary) 6%, transparent)',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <div>
                        <div className="small fw-bold text-uppercase rules-guide-kicker is-guide" style={{ letterSpacing: '0.08em' }}>
                          Guide
                        </div>
                        <div className="fw-semibold">วิธีการดูผลรางวัล</div>
                      </div>
                      <div
                        className="small rules-guide-chip is-guide"
                        style={{ backgroundColor: 'color-mix(in srgb, var(--htd-secondary) 10%, transparent)', padding: '6px 10px', borderRadius: 999 }}
                      >
                        เลื่อนขึ้นลงเพื่ออ่านต่อ
                      </div>
                    </div>
                    <div style={{ lineHeight: 1.7, overflowX: 'auto' }}>
                      {hasFetchedGuides ? renderedGuides : fallbackGuides}
                    </div>
                  </section>
                )}
              </div>
            ) : (
              <div className="text-center py-5 rules-guide-empty">ไม่พบข้อมูลกฎหรือวิธีการดูผลรางวัล</div>
            )}
          </div>
          <div
            className="modal-footer rules-guide-footer"
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 3,
              backgroundColor: 'rgba(9, 16, 36, 0.96)',
              borderTop: '1px solid rgba(125, 211, 252, 0.16)',
              padding: '14px 20px',
            }}
          >
            <button
              type="button"
              className="btn"
              style={{
                minWidth: 140,
                borderRadius: 999,
                border: 'none',
                color: '#fff',
                fontWeight: 700,
                background: 'linear-gradient(135deg, var(--htd-primary) 0%, var(--htd-secondary) 100%)',
                boxShadow: '0 10px 22px color-mix(in srgb, var(--htd-primary) 32%, transparent)',
              }}
              onClick={onClose}
            >
              {closeButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RulesGuideModal
